import { Component, OnInit, Input, ChangeDetectionStrategy, ElementRef, ViewChild } from '@angular/core';
import type { MatDatepicker } from '@angular/material/datepicker';
import { RequestRoot } from 'types';
import { AppContext } from '../control/context';
import { MaskedDate, NullableMaskedDate } from '../model/features-decl';
import { DatePickerType, FeaturesImpl } from '../model/features-impl';
import { ValidationService } from '../validation/validation.service';

@Component({
  selector: 'i-field-date-range',
  templateUrl: './fieldDateRange.component.html',
  styleUrls: ['./fieldDateRange.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class FieldDateRangeComponent implements OnInit {
  @Input() model: RequestRoot;
  @Input() pathFrom: string;
  @Input() pathTo: string;
  @Input() i18n_name: string;
  @Input() useDraft = true;

  private pathFromSteps: string[];
  private pathToSteps: string[];
  private datePickerType: DatePickerType;

  @ViewChild('from') from: ElementRef<HTMLInputElement>;
  @ViewChild('to') to: ElementRef<HTMLInputElement>;

  _i18n_name: string;

  constructor(private appContext: AppContext, private validationService: ValidationService) {}

  visible(): boolean {
    const v1 = this.pathFromSteps.reduce((pv, cv) => (pv === undefined ? undefined : pv[cv]), this.model.features);
    const v2 = this.pathToSteps.reduce((pv, cv) => (pv === undefined ? undefined : pv[cv]), this.model.features);
    return v1 !== undefined && v2 !== undefined;
  }

  disabled(): boolean {
    const v1 = this.pathFromSteps.reduce((pv, cv) => (pv === undefined ? undefined : pv[cv]), this.model.features);
    const v2 = this.pathToSteps.reduce((pv, cv) => (pv === undefined ? undefined : pv[cv]), this.model.features);
    return (
      (v1 !== undefined && v1 instanceof NullableMaskedDate && v1.value === null) ||
      (v2 !== undefined && v2 instanceof NullableMaskedDate && v2.value === null)
    );
  }

  click(_dp: any) {
    const dp = _dp as MatDatepicker<moment.Moment>;
    const v1 = this.pathFromSteps.reduce((pv, cv) => pv[cv], this.model.features);
    const v2 = this.pathToSteps.reduce((pv, cv) => pv[cv], this.model.features);
    if ((v1 instanceof MaskedDate || v1 instanceof NullableMaskedDate) && (v2 instanceof MaskedDate || v2 instanceof NullableMaskedDate)) {
      if (v1.value) {
        this.from.nativeElement.value = v1.value.replace('-00-', '-01-').replace('-00', '-01');
        this.from.nativeElement.dispatchEvent(new Event('input'));
      }
      if (v2.value) {
        this.to.nativeElement.value = v2.value.replace('-00-', '-01-').replace('-00', '-01');
        this.to.nativeElement.dispatchEvent(new Event('input'));
      }
      dp.open();
    } else {
      console.log('Invalid field types');
    }
  }

  private dpDaySelected(value: moment.Moment, path: string, pathSteps: string[]) {
    let text = '';
    if (value) {
      switch (this.datePickerType) {
        case DatePickerType.YMD:
          text = value.format('DD MM YYYY');
          break;
        case DatePickerType.YM:
          text = value.format('MM YYYY');
          break;
        case DatePickerType.Y:
          text = value.format('YYYY');
          break;
      }
    }
    const it = pathSteps.slice(0, -1).reduce((pv, cv) => pv[cv], this.model.features);
    it[pathSteps.slice(-1)[0]].value = FeaturesImpl.parseMaskedDate(path, this.model.features, text, this.appContext.getLocale());
    this.validationService.validate();
    if (this.useDraft) {
      this.appContext.storeDraft(this.model);
    }
  }

  dpFromDaySelected(event: { value: moment.Moment }) {
    this.dpDaySelected(event.value, this.pathFrom, this.pathFromSteps);
  }

  dpToDaySelected(event: { value: moment.Moment }) {
    this.dpDaySelected(event.value, this.pathTo, this.pathToSteps);
  }

  ngOnInit() {
    this.pathFromSteps = this.pathFrom.split('.');
    this.pathToSteps = this.pathTo.split('.');
    if (this.i18n_name) {
      this._i18n_name = this.appContext.getI18NString(this.i18n_name);
    }
    const datePickerType1 = FeaturesImpl.queryMaskedDate(this.pathFrom, this.model.features);
    this.datePickerType = datePickerType1.type;
  }
}
